import { AppState } from './app';
import * as constants from '../constants';
import { replaceUmlauts } from '../utils/react';
import {
  SchemaChangeDateBasketentryResponse,
  SchemaDigitalAvailabilityResponse,
  SchemaPriceForProductResponse,
  SchemaProduct,
  SchemaProductGroupNode,
  SchemaProductResponse,
  SchemaSelectResponse,
} from '@repo/legacy-commerce-api';

// We type the payload cast inside each switch case:
export type ProductActionType = {
  type:
    | typeof constants.POST_SELECT
    | typeof constants.FETCHED_PRODUCT
    | typeof constants.CHANGED_PRODUCT_DATE
    | typeof constants.FETCHED_PRODUCT_PRICE
    | typeof constants.FETCHED_DIGITAL_PRODUCT_AVAILABILITY
    | typeof constants.FETCHED_PRODUCT_FILTERS;
  payload: any;
};

/**
 * This is the main point where the product lists and all it's records are returned. It's functions as a 3 level deep nested array.
 *
 * L1: This is the <Format> or <Medium> layer, holds Digital, Analog, Traffic or Surface entires.
 * L2: Holds the actual search <Tag> or <Object> like if you searched for Zurich or Bern you'll see those there
 * L3: The actual <Product> Level, here we read out the SchemaProducts
 */
export const productReducer = (state: AppState, action: ProductActionType) => {
  switch (action.type) {
    case constants.POST_SELECT: {
      let products: Record<string, SchemaProduct> = {};
      let productsArray: SchemaProduct[] = [];
      let analog: Record<string, boolean> = {};
      let digital: Record<string, boolean> = {};
      let traffic: Record<string, boolean> = {};
      let surface: Record<string, boolean> = {};

      const { data } = action.payload as SchemaSelectResponse;

      data.elements.forEach((elementL1: SchemaProductGroupNode) => {
        elementL1.subgroup.elements.forEach((elementL2: SchemaProductGroupNode) => {
          if (elementL2.metadata) {
            let newLocations = state.userDataEC.locations;
            Object.keys(newLocations).forEach((locationName) => {
              if (locationName === replaceUmlauts(elementL2.metadata.name)) {
                newLocations[locationName].border = elementL2.metadata.border;
              }
            });
          }
          elementL2.subgroup.elements.forEach((elementL3: SchemaProductGroupNode) => {
            // @ts-expect-error product can only be resolves once unknown | unknown is solved on OpenAPI
            elementL3.products.forEach((product: SchemaProduct) => {
              products[product.producturn] = product;
              productsArray.push(product);
              if (product.producttype === 'analog') {
                analog[product.format] = true;
              } else if (product.producttype === 'digital') {
                digital[product.format] = true;
              } else {
                traffic[product.format] = true;
              }

              if (product.attributeshortcuts === 'L') {
                surface['Leuchtend (L)'] = true;
              } else if (product.attributeshortcuts === 'LR') {
                surface['Leuchtend / Rotierend (LR)'] = true;
              } else {
                surface['Normalplakat'] = true;
              }
            });
          });
        });
      });

      return {
        ...state,
        postSelectResponseElements: data.elements,
        productsJ2: [...productsArray],
        productsJ2ByUrn: { ...products },
        availableAttributesJ2: { analog, digital, traffic, surface },
        userDataEC: {
          ...state.userDataEC,
          shouldCreateNewBasketJ2: false,
        },
      };
    }

    case constants.FETCHED_PRODUCT: {
      const { data } = action.payload as SchemaProductResponse;
      // @ts-expect-error elements can only be resolves once unknown | unknown is solved on OpenAPI
      const productUrn = data.producturn;

      const productsByUrn =
        state.userDataEC.chosenBasketProducts && !state.productsJ2
          ? state.userDataEC.chosenBasketProducts.productsByUrn
          : state.productsJ2ByUrn;
      const products: SchemaProduct[] =
        state.userDataEC.chosenBasketProducts && !state.productsJ2
          ? state.userDataEC.chosenBasketProducts.products
          : state.productsJ2;

      const idx = Object.keys(productsByUrn).findIndex((productKey) => productKey == productUrn);
      const next = productsByUrn[products[idx + 1]?.producturn];
      const nextProduct: SchemaProduct | null = next ? next : null;

      const prev = productsByUrn[products[idx - 1]?.producturn];
      const prevProduct: SchemaProduct | null = prev ? prev : null;

      return {
        ...state,
        product: {
          [productUrn]: {
            ...action.payload.data,
            idx: idx,
          },
        },
        prevProduct: { ...prevProduct, idx: idx - 1 },
        nextProduct: { ...nextProduct, idx: idx + 1 },
      };
    }

    case constants.CHANGED_PRODUCT_DATE: {
      const { data } = action.payload as SchemaChangeDateBasketentryResponse;
      const productUrn: string = data.producturn;

      return {
        ...state,
        userDataEC: {
          ...state.userDataEC,
          basketProducts: {
            ...state.userDataEC.basketProducts,
            productsByUrn: {
              ...state.userDataEC.basketProducts.productsByUrn,
              [productUrn]: {
                ...state.userDataEC.basketProducts.productsByUrn[productUrn],
                startdate: data.startdate,
              },
            },
            productsByUrnDate: {
              ...state.userDataEC.basketProducts.productsByUrnDate,
              [productUrn]: {
                ...state.userDataEC.basketProducts.productsByUrnDate[productUrn],
              },
            },
          },
        },
      };
    }

    case constants.FETCHED_PRODUCT_PRICE: {
      const { data } = action.payload as SchemaPriceForProductResponse;
      const productUrn: string = data.producturn;
      // @ts-expect-error basketKey does not exist on response type:
      const basketKey = data.basketKey;

      let updatedState = {
        ...state,
        productsJ2ByUrn: {
          ...state.productsJ2ByUrn,
          [productUrn]: {
            ...state.productsJ2ByUrn[productUrn],
            pricefordates: data.prices,
            defaulttimeslot: action.payload.timeslot,
            dailytransmission: data.prices[0].dailytransmission,
            weeklytransmission: data.prices[0].totaltransmission,
          },
        },
      };
      if (Object.keys(state.userDataEC.basketProducts.productsByUrnDate).includes(basketKey)) {
        updatedState.userDataEC = {
          ...state.userDataEC,
          basketProducts: {
            ...state.userDataEC.basketProducts,
            productsByUrnDate: {
              ...state.userDataEC.basketProducts.productsByUrnDate,
              [basketKey]: {
                ...state.userDataEC.basketProducts.productsByUrnDate[basketKey],
                pricefordates: data.prices,
                startdate: data.startdate,
                baskettimeslot: action.payload.timeslot,
              },
            },
          },
        };
      }
      if (Object.keys(state.userDataEC.basketProducts.productsByUrn).includes(productUrn)) {
        updatedState.userDataEC = {
          ...state.userDataEC,
          basketProducts: {
            ...state.userDataEC.basketProducts,
            productsByUrn: {
              ...state.userDataEC.basketProducts.productsByUrn,
              [productUrn]: {
                ...state.userDataEC.basketProducts.productsByUrn[productUrn],
                pricefordates: data.prices,
                startdate: data.startdate,
                // @ts-expect-error OpenAPI mismatch: baskettimeslot does not exist on SchemaProduct
                baskettimeslot: action.payload.timeslot,
              },
            },
          },
        };
      }
      return updatedState;
    }

    case constants.FETCHED_DIGITAL_PRODUCT_AVAILABILITY: {
      const { data } = action.payload as SchemaDigitalAvailabilityResponse;
      const productUrn: string = data.producturn;
      // @ts-expect-error basketKey does not exist on response type:
      const basketKey = data.basketKey;

      let updatedState = {
        ...state,
        productsJ2ByUrn: {
          ...state.productsJ2ByUrn,
          [productUrn]: {
            ...state.productsJ2ByUrn[productUrn],
            availability: data.availability,
            // @ts-expect-error rating does not exist on type
            rating: data.rating,
          },
        },
      };
      if (Object.keys(state.userDataEC.basketProducts.productsByUrn).includes(productUrn)) {
        updatedState.userDataEC = {
          ...state.userDataEC,
          basketProducts: {
            ...state.userDataEC.basketProducts,
            productsByUrn: {
              ...state.userDataEC.basketProducts.productsByUrn,
              [productUrn]: {
                ...state.userDataEC.basketProducts.productsByUrn[productUrn],
                // @ts-expect-error OpenAPI mismatch: availability does not exist on SchemaProduct, availabilities (plural) does:
                availability: data.availability,
                // @ts-expect-error rating does not exist on type
                rating: data.rating,
              },
            },
          },
        };
      }

      if (Object.keys(state.userDataEC.basketProducts.productsByUrnDate).includes(basketKey)) {
        return {
          userDataEC: {
            ...state.userDataEC,
            basketProducts: {
              ...state.userDataEC.basketProducts,
              productsByUrnDate: {
                ...state.userDataEC.basketProducts.productsByUrnDate,
                [basketKey]: {
                  ...state.userDataEC.basketProducts.productsByUrnDate[basketKey],
                  availability: data.availability,
                  // @ts-expect-error rating does not exist on type
                  rating: data.rating,
                },
              },
            },
          },
        };
      }
      return updatedState;
    }

    case constants.FETCHED_PRODUCT_FILTERS: {
      return {
        ...state,
        userDataEC: {
          ...state.userDataEC,
          productFilters: action.payload,
        },
      };
    }
  }
};
